/**
 * Only the Tome I Owner may explicitly link the EXISTING first Editorial Story
 * to a private root BookPage. Opening this workspace is strictly GET-only.
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ALMOND_PRODUCT_ID } from "@/lib/almondPublicationCatalogV1.mjs";
import { EditorialStoryRender } from "@/components/editorial/EditorialStoryMaster";

const STORY_ID = "9c60f83f-b5ab-438b-b27e-09f41480a620";
const BLOCK_ID = "a9cadca9-3b6c-4504-8ed0-134a9899c060";

export function TomeIntroductionLinkControlV1({ product }) {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [volumeId, setVolumeId] = useState("");
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [review, setReview] = useState(null);
  const [approvedId, setApprovedId] = useState("");
  const [previewDevice, setPreviewDevice] = useState("desktop");

  if (product?.id !== ALMOND_PRODUCT_ID) return null;

  const check = async () => {
    if (working) return;
    setWorking(true);
    setMessage("");
    setState(null);
    setReview(null);
    setApprovedId("");
    try {
      const { data: volumes } = await api.get("/admin/book/volumes");
      const matched = (Array.isArray(volumes) ? volumes : []).filter(
        v => v.product_id === product.id
      );
      if (matched.length !== 1) {
        setVolumeId("");
        setMessage("Сначала подтвердите связь содержания Тома с Book.");
        return;
      }
      const volume = matched[0];
      if (volume.status !== "draft") {
        setMessage("Книжный Том уже изменил статус. Привязка остановлена.");
        return;
      }
      const { data } = await api.get(
        `/admin/book/volumes/${encodeURIComponent(volume.id)}/editorial-intro-draft`
      );
      if (data?.material_id !== STORY_ID) throw Error("Получена неверная статья.");
      setVolumeId(volume.id);
      setState(data);
    } catch (error) {
      setMessage(formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  const connect = async () => {
    if (working || !volumeId || !state || state.linked) return;
    setWorking(true);
    setMessage("");
    try {
      const { data } = await api.post(
        `/admin/book/volumes/${encodeURIComponent(volumeId)}/editorial-intro-draft`,
        { confirmed: true }
      );
      if (!data?.linked || data?.reader_ready !== false || data?.status !== "draft") {
        throw Error("Черновая связь не подтверждена.");
      }
      setState(current => ({ ...current, linked: true, page_id: data.page_id }));
      setMessage("Вступление связано с Томом как черновик. Читателям пока недоступно.");
    } catch (error) {
      setState(null);
      setMessage(formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  const reviewSavedStory = async () => {
    if (working || !state?.linked) return;
    setWorking(true);
    setReview(null);
    setApprovedId("");
    setMessage("");
    try {
      const { data } = await api.get(
        `/admin/editorial/spreads/${encodeURIComponent(BLOCK_ID)}/preview`
      );
      if (data?.mode !== "private_draft_preview" || data?.block?.id !== BLOCK_ID
        || data?.block?.material_id !== STORY_ID
        || data?.block?.master_type !== "EDITORIAL_STORY"
        || !data?.preview_token) throw Error("Версия статьи не подтверждена.");
      setReview(data);
    } catch (error) {
      setMessage(formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  const approveReviewedStory = async () => {
    if (working || !review?.preview_token || !state?.linked) return;
    setWorking(true);
    setMessage("");
    try {
      const { data } = await api.post(
        `/admin/editorial/spreads/${encodeURIComponent(BLOCK_ID)}/approve`,
        { preview_token: review.preview_token }
      );
      if (!data?.snapshot_id || data?.mode !== "private_approval_only") {
        throw Error("Утверждённая версия не подтверждена.");
      }
      setApprovedId(data.snapshot_id);
      setReview(null);
      setMessage("Версия утверждена и сохранена неизменяемо. Публикация не выполнялась.");
    } catch (error) {
      setReview(null);
      setMessage("Не удалось утвердить версию. Проверьте статью повторно. " + formatApiError(error));
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="mt-5 rounded-xl border border-[#D7C5AD] bg-white/40 p-4 text-sm text-[#644B36]"
      data-testid="tome-intro-draft-control">
      <h3 className="font-serif text-lg text-[#39261B]">Вступление к Тому I</h3>
      <p className="mt-1 text-xs leading-5">«Тихо скрипнула старая дверь...» — первая авторская история.
        Связь с книгой не меняет статью и не публикует её.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={check} disabled={working}
          data-testid="tome-intro-check"
          className="rounded-lg border border-[#9B7B59]/40 bg-white/60 px-4 py-2 disabled:opacity-50">
          {working ? "Проверяем…" : "Проверить вступление"}
        </button>
        <button type="button"
          onClick={() => nav(`/admin/editorial-v2/materials/${STORY_ID}`)}
          className="rounded-lg border border-[#9B7B59]/40 bg-white/60 px-4 py-2">
          Открыть статью в Редакции
        </button>
      </div>
      {state && (
        <div className="mt-3 text-xs" data-testid="tome-intro-state">
          {state.linked
            ? "Статья привязана к началу Тома. Пока только черновик."
            : "Вступление ещё не привязано. Будет создана ровно одна черновая BookPage."}
          {!state.linked && (
            <div className="mt-3">
              <p className="mb-2">Нажатие создаст связь только с существующей статьёй.
                Никакие публикации, читательские доступы и категории не изменятся.</p>
              <button type="button" onClick={connect} disabled={working}
                data-testid="tome-intro-connect"
                className="rounded-lg bg-[#4A3425] px-4 py-2 text-white disabled:opacity-50">
                {working ? "Подключаем…" : "Подключить вступление"}
              </button>
            </div>
          )}
        </div>
      )}
      {state?.linked && (
        <div className="mt-4 border-t border-[#9B7B59]/20 pt-4" data-testid="tome-intro-owner-approval">
          <p className="text-xs">Сначала проверьте сохранённый разворот, затем явно утвердите его версию.
            Reader покажет только неизменяемую утверждённую версию, а не текущий черновик.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={reviewSavedStory} disabled={working}
              data-testid="tome-intro-review-saved"
              className="rounded-lg border border-[#9B7B59]/40 bg-white/60 px-4 py-2 disabled:opacity-50">
              Проверить сохранённую статью
            </button>
            <button type="button"
              onClick={() => nav(`/book/${encodeURIComponent(product.slug)}/pages/${encodeURIComponent(state.page_id)}`)}
              disabled={working || !state.page_id}
              data-testid="tome-intro-open-reader"
              className="rounded-lg border border-[#9B7B59]/40 bg-white/60 px-4 py-2 disabled:opacity-50">
              Открыть закрытый Reader
            </button>
          </div>
          {review && (
            <div className="mt-4" data-testid="tome-intro-unapproved-review">
              <p className="mb-2 text-xs font-medium">Проверяемая сохранённая версия · ещё не утверждена</p>
              <div className="mb-3 flex gap-2">
                <button type="button" onClick={() => setPreviewDevice("desktop")}
                  className="rounded-lg border border-[#9B7B59]/40 px-3 py-1.5"
                  data-testid="tome-intro-review-desktop">Desktop</button>
                <button type="button" onClick={() => setPreviewDevice("mobile")}
                  className="rounded-lg border border-[#9B7B59]/40 px-3 py-1.5"
                  data-testid="tome-intro-review-mobile">Mobile</button>
              </div>
              <div className={previewDevice === "mobile" ? "mx-auto max-w-[380px]" : "w-full"}>
                <EditorialStoryRender block={review.block} mode={previewDevice} />
              </div>
              <button type="button" disabled={working} onClick={approveReviewedStory}
                data-testid="tome-intro-approve-frozen"
                className="mt-3 rounded-lg bg-[#4A3425] px-4 py-2 text-white disabled:opacity-50">
                Утвердить проверенную версию
              </button>
            </div>
          )}
          {approvedId && (
            <p className="mt-3 text-xs" data-testid="tome-intro-approved">
              Утверждённая версия готова для закрытого Reader. Публикация выключена.
            </p>
          )}
        </div>
      )}
      {message && <p role="status" className="mt-2 text-xs">{message}</p>}
    </section>
  );
}
